import { useState, useMemo } from "react";

import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import SaveIcon from "@mui/icons-material/Save";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  Typography,
} from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";

import { AdminProtected } from "@/components/Application/AdminProtected";
import { Translate } from "@/components/Application/Translate";
import { ProcedureNodeFolderItem } from "@/components/Procedures/ProcedureNodeFolders/ProcedureNodeFolderItem";
import { isLink } from "@/components/Procedures/helpers/helperFunctions";

import { AlertRequest } from "@/helpers/alerts/AlertRequest";
import { AlertToast } from "@/helpers/alerts/AlertToast";
import CustomCheckbox from "@/helpers/components/CustomFormFields/CustomCheckbox";
import CustomInput from "@/helpers/components/CustomFormFields/CustomInput";
import CustomSelect from "@/helpers/components/CustomFormFields/CustomSelect";

import { ProcedureGroup } from "@/model/Procedure/ProcedureGroup";

import {
  SaveProcedureGroup,
  UseUpdateProcedureGroup,
} from "@/service/ProcedureMutations";
import {
  useFetchActiveProcedureGroupFolders,
  useFetchProcedureGroupDetails,
  useFetchProcedureMetaData,
} from "@/service/ProcedureQueries";

type EditProcedureFolderProps = {
  handleClose: () => void;
  open: boolean;
  isLink(): boolean;
  id: number;
};

const EditProcedureFolder = (props: EditProcedureFolderProps) => {
  return (
    <AdminProtected>
      <Dialog
        onClose={props.handleClose}
        open={props.open}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <EditProcedureFolderDialogContent
          handleClose={props.handleClose}
          isLink={props.isLink}
          id={props.id}
        />
      </Dialog>
    </AdminProtected>
  );
};

export default EditProcedureFolder;

type EditProcedureFolderDialogContentProps = {
  handleClose: () => void;
  isLink(): boolean; // kept for API compatibility (not used)
  id: number;
};

const EditProcedureFolderDialogContent = (
  props: EditProcedureFolderDialogContentProps
) => {
  const [permissionsOpen, togglePermissions] = useState<boolean>(false);
  const [allSelected, setAllSelected] = useState<boolean>(false);

  const { handleClose, id } = props;

  const { data: procedureMetaDataStore } = useFetchProcedureMetaData("?");
  const { groupList } = useFetchActiveProcedureGroupFolders();
  const { data: loadProcedureFolder, isLoading: isLoadingDetails } =
    useFetchProcedureGroupDetails(id);

  const { updateProcedureFolder } = UseUpdateProcedureGroup();
  const save = SaveProcedureGroup();

  // compute on every render (safe while loading) to keep hook order stable
  const isLinkItem =
    !!loadProcedureFolder && isLink(loadProcedureFolder.Id, groupList);

  // validation schema (hook must be called every render)
  const validationSchema = useMemo(
    () =>
      Yup.object({
        Name: Yup.string().trim().required("Name is required"),
        Link: isLinkItem
          ? Yup.string()
              .trim()
              .required("Link is required")
              .url("Enter a valid URL")
              .matches(/^https?:\/\//i, "URL must start with http:// or https://")
          : Yup.string().trim().nullable(),
        ParentId: Yup.number().nullable(),
      }),
    [isLinkItem]
  );

  const handleDelete = async (values: ProcedureGroup) => {
    const payload: ProcedureGroup = { ...values, DoDelete: true };
    try {
      handleClose();
      AlertRequest.confirm(save, payload);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      AlertRequest.failed();
    }
  };

  const handleSave = async (values: ProcedureGroup) => {
    try {
      // defense-in-depth: still validate link on submit
      if (isLinkItem) {
        const link = (values?.Link ?? "").trim();
        const valid = /^https?:\/\/.+/i.test(link);
        if (!link || !valid) {
          AlertToast.failed?.("Please provide a valid http(s) link before saving.");
          return;
        }
      }
      handleClose();
      await updateProcedureFolder(values);
      AlertToast.success();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      AlertToast.failed();
    }
  };

  const handleToggleAll = () => setAllSelected((v) => !v);

  if (isLoadingDetails || !loadProcedureFolder) {
    return (
      <>
        <DialogTitle sx={{ pr: 6, pb: 0.5, fontWeight: 700 }}>
          <Translate>Loading…</Translate>
        </DialogTitle>
        <DialogContent dividers>
          {[...Array(6)].map((_, i) => (
            <Skeleton
              key={i}
              variant="rectangular"
              height={40}
              sx={{ mb: 1.25, borderRadius: 1.5 }}
            />
          ))}
        </DialogContent>
      </>
    );
  }

  return (
    <>
      {/* Header */}
      <DialogTitle
        sx={{
          pr: 6,
          py: 1.25,
          fontWeight: 800,
          bgcolor: (t) =>
            t.palette.mode === "light"
              ? "#F8FAFC"
              : t.palette.background.default,
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
        }}
      >
        {isLinkItem ? (
          <Translate String="Procedures.EditLink.Header">Edit Link</Translate>
        ) : (
          <Translate String="Procedures.EditGroup.Header">Edit group</Translate>
        )}
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{ position: "absolute", right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>

      {/* Body */}
      <DialogContent dividers sx={{ pt: 2 }}>
        <Formik
          initialValues={{
            Name: loadProcedureFolder?.Name ?? "",
            Link: loadProcedureFolder?.Link ?? "",
            Id: id,
            PermisionList: loadProcedureFolder?.PermisionList ?? [],
            ParentId: loadProcedureFolder?.ParentId || 0,
            SortNumber: loadProcedureFolder?.SortNumber ?? 0,
            CanDeletes: loadProcedureFolder?.CanDeletes ?? true,
            DoDelete: loadProcedureFolder?.DoDelete ?? false,
          }}
          onSubmit={handleSave}
          validationSchema={validationSchema}
          validateOnMount
        >
          {({ values, isValid, isSubmitting }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <CustomInput
                    id="Name"
                    type="text"
                    label="Name"
                    name="Name"
                    translationString="Procedures.Group.Name"
                    placeholder={isLinkItem ? "e.g. Safety Handbook" : "e.g. Operations"}
                    fullWidth
                    onKeyDown={(e: any) => e.stopPropagation()}
                  />
                  <Typography variant="caption" color="text.secondary">
                    <Translate>Choose a clear, concise name.</Translate>
                  </Typography>
                </Grid>

                {isLinkItem && (
                  <Grid item xs={12} sm={6}>
                    <CustomInput
                      id="Link"
                      type="url"
                      label="Link"
                      name="Link"
                      translationString="Procedures.Group.Link"
                      placeholder="https://example.com/resource"
                      fullWidth
                      onKeyDown={(e: any) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LinkOutlinedIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      <Translate>Paste a full URL starting with http(s)://</Translate>
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12} sm={6}>
                  <CustomSelect
                    label="Folder"
                    name="ParentId"
                    id="procedureFolder"
                    translationString="Procedures.Group.Folder"
                    fullWidth
                  >
                    {procedureMetaDataStore?.Folders
                      ?.filter(
                        (folder) =>
                          folder.GroupNumber !== id &&
                          !isLink(folder.GroupNumber, groupList)
                      )
                      .map((folder) => (
                        <ProcedureNodeFolderItem
                          Folder={folder}
                          key={folder.GroupNumber.toString()}
                        />
                      ))}
                  </CustomSelect>
                  <Typography variant="caption" color="text.secondary">
                    <Translate>
                      Move this item under a different folder (you can’t select itself or a link).
                    </Translate>
                  </Typography>
                </Grid>
              </Grid>

              {/* Permissions */}
              <Accordion
                expanded={permissionsOpen}
                onChange={(_, exp) => togglePermissions(exp)}
                sx={{
                  mt: 2,
                  border: (t) => `1px solid ${t.palette.divider}`,
                  boxShadow: "none",
                  "&:before": { display: "none" },
                  borderRadius: 2,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: (t) =>
                      t.palette.mode === "light"
                        ? "#F8FAFC"
                        : t.palette.background.default,
                    borderBottom: (t) => `1px solid ${t.palette.divider}`,
                    minHeight: 54,
                    "& .MuiAccordionSummary-content": {
                      my: 0.75,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    },
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={800}>
                    <Translate String="Procedures.Group.Permission">Permissions</Translate>
                  </Typography>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 2 }}>
                  <Grid container spacing={1} sx={{ pb: 1 }}>
                    <Grid item xs={7}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        <Translate String="Procedures.Group.Permision.Groups">
                          Show for selected groups
                        </Translate>
                      </Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={700}
                        sx={{ display: "block", textAlign: "center" }}
                      >
                        <Translate String="Procedures.Group.Permision.Details">
                          Show for all if none selected
                        </Translate>
                      </Typography>
                    </Grid>
                  </Grid>

                  {values?.PermisionList?.map((permission: any, idx: number) => (
                    <Grid
                      container
                      key={`${permission?.Name}-${idx}`}
                      alignItems="center"
                      sx={{
                        py: 1,
                        px: 1,
                        borderRadius: 1.5,
                        "&:nth-of-type(odd)": {
                          bgcolor: (t) =>
                            t.palette.mode === "light"
                              ? "#F9FAFB"
                              : t.palette.action.hover,
                        },
                      }}
                    >
                      <Grid item xs={7}>
                        <Typography variant="body2" fontWeight={600}>
                          {permission?.Name}
                        </Typography>
                      </Grid>
                      <Grid item xs={5} sx={{ display: "flex", justifyContent: "center" }}>
                        {/* Visual “select all” via allSelected; preserves current behavior */}
                        <CustomCheckbox
                          label=""
                          name={permission?.Name}
                          type="checkbox"
                          id={permission?.Name}
                          checked={allSelected}
                        />
                      </Grid>
                    </Grid>
                  ))}

                  {!values?.PermisionList?.length && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <Translate>No permission entries.</Translate>
                    </Typography>
                  )}
                </AccordionDetails>
              </Accordion>

              {/* Footer */}
              <DialogActions
                sx={{
                  mt: 2,
                  pt: 2,
                  borderTop: (t) => `1px solid ${t.palette.divider}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box>
                  {loadProcedureFolder.CanDeletes && (
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => handleDelete(values)}
                      sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                    >
                      {isLinkItem ? (
                        <Translate String="Procedure.Button.Delete.link">Delete link</Translate>
                      ) : (
                        <Translate String="Procedure.Button.Delete.folder">Delete folder</Translate>
                      )}
                    </Button>
                  )}
                </Box>

                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<CloseIcon />}
                    onClick={handleClose}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                  >
                    <Translate String="Procedure.Button.Close">Close</Translate>
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<SaveIcon />}
                    type="submit"
                    disabled={!isValid || isSubmitting}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                  >
                    <Translate String="Procedure.Button.Save">Save</Translate>
                  </Button>
                </Box>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </>
  );
};
